"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileSearch, ShieldCheck, Scale, RefreshCw, ArrowRight, CircleCheck as CheckCircle2, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const policies = [{
  id: "methodology",
  iconName: "FileSearch",
  title: "Empirical research methodology",
  subtitle: "Primary data & peer evaluation",
  description: "Our analysts source primary market data directly from verified B2B practitioners, industry leaders, and public registries. Every quantitative dataset undergoes peer scrutiny before publication.",
  detail1: "Direct practitioner survey panels",
  detail2: "Verifiable data lineage and sourcing",
  detail3: "Reproducible analytical models"
}, {
  id: "independence",
  iconName: "Scale",
  title: "Editorial independence",
  subtitle: "Complete commercial separation",
  description: "We maintain a rigid barrier between our editorial research desk and commercial partnerships. Advertisers and sponsors have zero preview privileges, input, or veto power over research findings.",
  detail1: "No paid placement in rankings",
  detail2: "Strict conflict of interest disclosures",
  detail3: "Unbiased vendor assessments"
}, {
  id: "benchmarks",
  iconName: "ShieldCheck",
  title: "Verification benchmarks",
  subtitle: "Multi-tier fact checking",
  description: "Every manuscript passes through dedicated subject-matter editors and technical fact-checkers. We corroborate claims against secondary sources and official industry documentation.",
  detail1: "Cross-referenced data points",
  detail2: "Executive editor sign-off required",
  detail3: "Clear attribution for all citations"
}, {
  id: "corrections",
  iconName: "RefreshCw",
  title: "Corrections and update logs",
  subtitle: "Transparent audit trails",
  description: "When substantive errors occur or market circumstances change, we issue public correction notices with timestamped changelogs. Reports are systematically reviewed each quarter.",
  detail1: "Public errata and changelog notes",
  detail2: "Quarterly report validity checks",
  detail3: "Direct reader inquiry review line"
}];
export default function Standards() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: string) => {
    switch (name) {
      case "FileSearch":
        return <FileSearch className="size-5 text-primary" aria-hidden="true" />;
      case "Scale":
        return <Scale className="size-5 text-primary" aria-hidden="true" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" aria-hidden="true" />;
      case "RefreshCw":
        return <RefreshCw className="size-5 text-primary" aria-hidden="true" />;
      default:
        return <FileSearch className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="publishing-standards-editorial-transparency" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Editorial Masthead Header */}
        <div className="mx-auto max-w-[840px] text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            Institutional Integrity
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-tight mb-5">
            How we research, write, and verify our work
          </h2>
          
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every report, guide, and analysis is held to strict editorial principles so you can cite our findings with confidence.
          </p>

          {/* Masthead Rule Motif */}
          <div className="mt-8 flex flex-col items-center gap-1">
            <div className="w-24 h-[1px] bg-primary" />
            <div className="w-12 h-[0.5px] bg-border" />
          </div>
        </div>

        {/* 4 Policy Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {policies.map((policy, i) => <motion.div key={policy.id} data-index={i} variants={cardVariants} className="h-full">
              <Card className="h-full flex flex-col justify-between rounded-md border border-border bg-card p-6 sm:p-8 shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-200 hover:border-primary/40">
                <div>
                  <CardHeader className="p-0 gap-3 mb-5">
                    <div className="flex items-center justify-between">
                      <div className="flex size-10 items-center justify-center rounded-sm bg-muted border border-border">
                        {renderIcon(policy.iconName)}
                      </div>
                      <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                        Policy 0{i + 1}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl sm:text-2xl text-card-foreground font-semibold">
                      {policy.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {policy.subtitle}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-0">
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                      {policy.description}
                    </p>

                    <div className="border-t border-border pt-4 space-y-2.5">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" aria-hidden="true" />
                        <span>{policy.detail1}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" aria-hidden="true" />
                        <span>{policy.detail2}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-card-foreground">
                        <CheckCircle2 className="size-4 text-primary shrink-0" aria-hidden="true" />
                        <span>{policy.detail3}</span>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Editorial Contact & Errata Note */}
        <div className="mt-12 lg:mt-16 rounded-md bg-muted/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-foreground">
              <Mail className="size-4 text-primary" aria-hidden="true" />
              <span>Direct Editorial Inquiry & Corrections Desk</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Found a factual discrepancy or requesting clarification on a research dataset? Contact our editorial board directly at{" "}
              <a href="mailto:info@minfomedia.com" className="font-medium text-foreground underline underline-offset-4 hover:text-primary transition-colors">
                info@minfomedia.com
              </a>
              . Submissions are reviewed within 48 business hours.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-3 w-full md:w-auto">
            <Button asChild variant="outline" size="default" className="rounded-sm w-full md:w-auto">
              <Link to="/ResearchReportsPage">
                <span>Browse peer-reviewed reports</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}